import { getAuthCookieName } from "utils/cookieOptions";
 
import Cookies from "js-cookie";
import { IMqttPayload } from "types/common";

/**
 * Realtime transport.
 *
 * The browser no longer connects to the MQTT broker directly (which required
 * shipping broker credentials in the client bundle). It connects to the
 * client-api SSE proxy (`/realtime/mqtt`), authenticated with the investor's
 * JWT. The proxy holds the broker credentials server-side and enforces that an
 * investor may only read their own topics.
 *
 * The exported `mqttService` keeps the same surface the app already uses
 * (getClient / subscribe / onMessage / onOmnibusMessage / unsubscribe /
 * closeConnection) so call sites do not change.
 */

type AnyCallback = (payload: IMqttPayload<any>) => void;

/** Build the SSE endpoint URL from the API base (WEB_API_URL already ends in /api). */
export function getGatewayUrl(): string {
	const base = (process.env.WEB_API_URL ?? "").replace(/\/$/, "");
	return `${base}/realtime/mqtt`;
}

export class RealtimeClient {
	private es: EventSource | null = null;
	private readonly topicHandlers = new Map<string, Set<AnyCallback>>();
	private readonly prefixHandlers = new Set<{ prefix: string; cb: AnyCallback }>();
	private readonly topics = new Set<string>();
	private reopenTimer: ReturnType<typeof setTimeout> | null = null;

	connected = false;

	constructor(
		private readonly baseUrl: string,
		private readonly errorHandler?: (err: Error) => void,
	) {}

	/** The client is a logical handle; the SSE stream opens once topics are added. */
	markReady() {
		this.connected = true;
	}

	private buildUrl(): string {
		const token = Cookies.get(getAuthCookieName("token")) ?? "";
		const params = new URLSearchParams();
		if (token) params.set("token", token);
		this.topics.forEach((topic) => params.append("topic", topic));
		return `${this.baseUrl}?${params.toString()}`;
	}

	/** Debounce multiple subscribe/unsubscribe calls into a single reconnect. */
	private scheduleReopen() {
		if (this.reopenTimer) clearTimeout(this.reopenTimer);
		this.reopenTimer = setTimeout(() => this.reopen(), 0);
	}

	private reopen() {
		if (this.es) {
			this.es.close();
			this.es = null;
		}
		if (this.topics.size === 0) return;

		const es = new EventSource(this.buildUrl(), { withCredentials: true });
		this.es = es;
		es.addEventListener("message", (event) =>
			this.handleFrame((event as MessageEvent).data),
		);
		es.onerror = () => {
			if (this.errorHandler) this.errorHandler(new Error("Realtime SSE error"));
		};
	}

	private handleFrame(raw: string) {
		let frame: { topic?: string; data?: unknown };
		try {
			frame = JSON.parse(raw);
		} catch {
			return;
		}
		if (!frame?.topic) return;

		const payload = { topic: frame.topic, packet: undefined as any, message: frame.data as any };

		const exact = this.topicHandlers.get(frame.topic);
		if (exact) exact.forEach((cb) => cb(payload));

		this.prefixHandlers.forEach((handler) => {
			if (frame.topic!.startsWith(handler.prefix)) handler.cb(payload);
		});
	}

	subscribeTopic(topic: string) {
		if (!this.topics.has(topic)) {
			this.topics.add(topic);
			this.scheduleReopen();
		}
	}

	unsubscribeTopic(topic: string) {
		if (this.topics.delete(topic)) this.scheduleReopen();
		this.topicHandlers.delete(topic);
	}

	onTopic(topic: string, callBack: AnyCallback) {
		let set = this.topicHandlers.get(topic);
		if (!set) {
			set = new Set();
			this.topicHandlers.set(topic, set);
		}
		set.add(callBack);
	}

	onPrefix(prefix: string, callBack: AnyCallback) {
		this.prefixHandlers.add({ prefix, cb: callBack });
	}

	close() {
		if (this.reopenTimer) clearTimeout(this.reopenTimer);
		this.topics.clear();
		this.topicHandlers.clear();
		this.prefixHandlers.clear();
		if (this.es) {
			this.es.close();
			this.es = null;
		}
		this.connected = false;
	}
}

function getClient(
	url: string,
	_options?: unknown,
	errorHandler?: (err: Error) => void,
): Promise<RealtimeClient | null> {
	const client = new RealtimeClient(url, errorHandler);
	client.markReady();
	return Promise.resolve(client);
}

function subscribe(client: RealtimeClient, topic: string) {
	client.subscribeTopic(topic);
}

function onMessage(client: RealtimeClient, currentTopic: string, callBack: (payload: any) => void) {
	client.onTopic(currentTopic, callBack as AnyCallback);
}

function onOmnibusMessage<T>(
	client: RealtimeClient,
	currentTopic: string,
	callBack: (payload: IMqttPayload<T>) => void,
) {
	const [prefixTopic] = currentTopic.split("/");
	client.onPrefix(prefixTopic, callBack as AnyCallback);
}

function unsubscribe(client: RealtimeClient, topic: string) {
	client.unsubscribeTopic(topic);
}

function closeConnection(client: RealtimeClient) {
	client.close();
}

/**
 * Publishing from the browser is no longer supported: only backend services
 * publish to the broker. Kept as a no-op so any legacy call site is inert.
 */
function publish(_client: RealtimeClient, _topic: string, _message: string) {
	 
	console.warn("mqttService.publish is disabled: browsers cannot publish to the broker.");
}

export const mqttService = {
	getClient,
	subscribe,
	onMessage,
	unsubscribe,
	closeConnection,
	publish,
	onOmnibusMessage,
	getGatewayUrl,
};
